import React, { FC } from 'react';
import { ProductPageLink } from '../product/product-page-link';
import { ProductSearchItemFragment } from '../../@types/codegen/graphql';
import styled from '@emotion/styled';
import { generateProductTitle } from '../search-page/helper/generate-product-title';
import { spacingCalculator } from '@digando/react-component-library';
import { getFormattedPropertyValue } from '../../helper/readable-product-property';
import { RemoteImage } from '../../lib/remote-image';
import { TenantLogo } from '../../lib/image/tenant-logo';
import { ProductCard } from '../elements/ProductCard/ProductCard';
import { fallbackImageUrl } from '../../helper/fallback-image';
import { ProductCardPrice } from '../elements/ProductCard/product-card-price';
import dayjs from 'dayjs';
import { toDynamicPriceDomain } from '../elements/ProductCard/ProductPrice';

interface SmallProductCardProps {
  product: Pick<ProductSearchItemFragment, 'id' | 'hasHiddenPrice' | 'showPrices' | 'properties' | 'type' | 'productImages' | 'tenant' | 'rentalStation' | 'previewPriceInformation'>;
}

const SmallProductCardComponent: FC<SmallProductCardProps> = ({ product }) => {
  const { type } = product;

  const properties = product.properties
    .filter(property => property.isRolloverProperty)
    .slice(0, 3)
    .map((property, index) => ({
      id: index,
      name: property.descriptor.title ?? property.key,
      value: getFormattedPropertyValue({
        descriptor: property.descriptor,
        values: property.value,
      }),
    }));

  const productImage = product.productImages[0];

  const productPrice = (
    <PricesContainer>
      <ProductCardPrice
        productId={product.id}
        showPrices={product.showPrices}
        hasHiddenPrice={product.hasHiddenPrice}
        dateRange={{ gte: dayjs(), lte: dayjs() }}
        previewPriceInformation={product?.previewPriceInformation ? toDynamicPriceDomain(product.previewPriceInformation) : null}
      />
    </PricesContainer>
  );

  const productTitle = generateProductTitle({ title: product.type.title}, { title: product.type.manufacturer.title}, []);

  return (
    <ProductPageLink
      href={product.type.productPageUrl}
      variants={[]}
      rentalStationId={product.rentalStation.id}
      tenantKey={product.tenant.key}
      title={productTitle}
    >
      <ProductCard
        isFactListExpanded={true}
        tagName={type.mainCategory?.title ?? ''}
        productTitle={productTitle}
        productPrice={productPrice}
        productImg={
          <ProductImage>
            <RemoteImage
              sources={[
                { maxWidth: '440px', src: fallbackImageUrl({url: productImage.url, w: '330', q: '90', crop: false}) },
                { maxWidth: '1024px', src: fallbackImageUrl({url: productImage.url, w: '450', q: '90', crop: false}) },
              ]}
              src={fallbackImageUrl({url: productImage.url, w: '330', q: '90'})}
              alt={productImage.alt}
              title={productTitle}
              blurSrc={fallbackImageUrl({url: productImage.url, w: '330', blur: '300'})}
              width={productImage.metadata.width}
              height={productImage.metadata.height}
            />
          </ProductImage>
        }
        partnerLogo={
          <>
            {product.tenant?.logo && (
              <TenantLogoContainer>
                <TenantLogo logo={product.tenant.logo} height={30} width={130} />
              </TenantLogoContainer>
            )}
          </>
        }
        productInfoArray={properties}
      />
    </ProductPageLink>
  );
};

const PricesContainer = styled.div`
  max-width: 57%;
`;

const TenantLogoContainer = styled.div`
  position: relative;
  height: 30px;
  margin-left: ${spacingCalculator(4)};
  max-width: 130px;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

// @todo: Move to CL and reuse it on mobile search result
const ProductImage = styled.div`
  width: 100%;
  height: 280px;
  cursor: pointer;

  * {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }
`;

export const SmallProductCard = React.memo(SmallProductCardComponent, (prevProps, nextProps) => {
  return nextProps.product.id === prevProps.product.id
});
